import { Service, computed, signal } from '@angular/core';
import { AuthSession, OperatorRole, Principal } from '../models/auth.model';

const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const PRINCIPAL_KEY = 'auth_principal';

export type Zone = 'admin' | 'user';

// The /app/** zone is RenderMode.Client only (see app.routes.server.ts), so
// this never runs during SSR — the localStorage guards below are defensive
// regardless.
//
// Token storage: the access token lives in memory only (this signal, never
// persisted) — bounds its exposure to the current page load. The refresh
// token and principal persist in localStorage so a reload or a second tab can
// silently re-authenticate via POST /v1/auth/refresh instead of forcing a
// fresh login. This is short of OWASP's actual recommendation (HttpOnly/
// Secure/SameSite cookies, or a Backend-for-Frontend) specifically for the
// refresh token — neither is available here without the backend issuing
// Set-Cookie instead of a JSON body (see design-handoff/planificacion_fase_2.md
// §Endpoints), which is a contract change outside this frontend's control.
// Worth revisiting with whoever owns that contract if it becomes an option.
@Service()
export class Auth {
  private readonly _accessToken = signal<string | null>(null);
  readonly accessToken = this._accessToken.asReadonly();

  private readonly _refreshToken = signal<string | null>(this.readStored(REFRESH_TOKEN_KEY));
  readonly refreshToken = this._refreshToken.asReadonly();

  private readonly _principal = signal<Principal | null>(this.readPrincipal());
  readonly principal = this._principal.asReadonly();

  readonly isAuthenticated = computed(() => this._accessToken() !== null);

  readonly email = computed(() => this._principal()?.email ?? null);

  readonly zone = computed<Zone | null>(() => {
    const profileType = this._principal()?.profileType;
    if (profileType === 'backoffice_operator') {
      return 'admin';
    }
    if (profileType === 'final_user') {
      return 'user';
    }
    return null;
  });

  readonly operatorRole = computed<OperatorRole | null>(() => this._principal()?.role ?? null);
  readonly obraSocialId = computed(() => this._principal()?.obraSocialId ?? null);
  readonly isGeneralAdmin = computed(() => this.operatorRole() === 'general_admin');
  readonly passwordChangeRecommended = computed(() => this._principal()?.passwordChangeRecommended ?? false);

  applySession(session: AuthSession): void {
    this._accessToken.set(session.accessToken);
    this._refreshToken.set(session.refreshToken);
    this._principal.set(session.principal);
    this.writeStored(REFRESH_TOKEN_KEY, session.refreshToken);
    this.writeStored(PRINCIPAL_KEY, JSON.stringify(session.principal));
  }

  clear(): void {
    this._accessToken.set(null);
    this._refreshToken.set(null);
    this._principal.set(null);
    this.removeStored(REFRESH_TOKEN_KEY);
    this.removeStored(PRINCIPAL_KEY);
  }

  private readPrincipal(): Principal | null {
    const raw = this.readStored(PRINCIPAL_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as Principal;
    } catch {
      return null;
    }
  }

  private readStored(key: string): string | null {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  }

  private writeStored(key: string, value: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  }

  private removeStored(key: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  }
}
